"use client"

import { useEffect, useState } from "react"

import { CreateAddressRequest } from "@lib/clients/nelo-api-client"
import { sendGoogleAnalyticsEvent } from "@modules/common/utils/analytics"
import { useClientInitialization } from "@modules/signup-and-order-flow/util/use-client-initializations"
import { useCartCreation } from "@modules/signup-and-order-flow/util/use-create-cart"
import { useCreateMarketplaceLoan } from "@modules/signup-and-order-flow/util/use-create-maketpalce-loan"
import { useGetCartAddress } from "@modules/signup-and-order-flow/util/use-get-cart-address"
import { useLoanOptions } from "@modules/signup-and-order-flow/util/use-get-loan-options"
import { useGetUserAddresses } from "@modules/signup-and-order-flow/util/use-get-user-address"
import { useUpdateCartPaymentSession } from "@modules/signup-and-order-flow/util/use-update-payment-session"
import { useUpdateAddress } from "@modules/signup-and-order-flow/util/use-update-user-address"

import LoanOptionsDisplay from "./loan-options-display"
import LoadingScreen from "../loading"

interface LoanOptionsProps {
  variantId: string | null
  onComplete: (cartId: string) => Promise<void>
  onServerError: () => void
  onCartTotalChange: (total: number) => void
  preSelectedInstallment?: number
}

const LoanOptions = ({ variantId, onComplete, onServerError, onCartTotalChange, preSelectedInstallment }: LoanOptionsProps) => {
  const [selectedOptionUuid, setSelectedOptionUuid] = useState<string | null>(null)
  const [addressUpdateTrigger, setAddressUpdateTrigger] = useState(0)
  const [isCompleteLoading, setIsCompleteLoading] = useState(false)

  const { isInitialized, error: clientInitializationError } = useClientInitialization()
  const { isLoading: isAddressLoading, addresses, error: addressesError, isAddressSet } = useGetUserAddresses(isInitialized)
  const {
    isLoading,
    cartId,
    cartDetails,
    isShippingMethodSet,
    isPaymentSessionCreated,
    error: cartCreationError,
  } = useCartCreation(variantId, isInitialized, addresses, isAddressSet)
  const {
    cartAddress,
    isLoading: isCartAddressLoading,
    error: cartAddressError,
  } = useGetCartAddress(cartId, addressUpdateTrigger, isShippingMethodSet)
  const { loanOptions, isLoanOptionLoading, error: loanOptionsError } = useLoanOptions(cartId, cartDetails?.total ?? 0, isInitialized)
  const {
    isLoading: isLoanResponseLoading,
    loanResponse,
    error: loanResponseError,
  } = useCreateMarketplaceLoan(selectedOptionUuid, cartId, cartDetails?.total ?? 0)
  const {
    isPaymentSessionUpdated,
    isuseUpdateCartPaymentSessionLoading,
    error: updateCartPaymentSessionError,
  } = useUpdateCartPaymentSession({
    cartId,
    loanUuid: loanResponse?.loanInfo.loanUuid ?? "",
    isPaymentSessionCreated,
  })
  const [isReadyToComplete, setIsReadyToComplete] = useState<boolean>(false)

  const { submitAddress, isLoading: isSubmitAddressLoading, error: submitAddressError } = useUpdateAddress()
  const handleSubmitAddress = async (address: CreateAddressRequest) => {
    try {
      sendGoogleAnalyticsEvent("SUBMIT_ADDRESS")
      const result = await submitAddress(cartId!, address)
      if (result) {
        setAddressUpdateTrigger((prev) => prev + 1)
      }
    } catch (error) {
      sendGoogleAnalyticsEvent("SUBMIT_ADDRESS_ERROR")
      console.error("Error updating address:", error)
    }
  }

  useEffect(() => {
    const errors = [
      submitAddressError,
      loanResponseError,
      cartAddressError,
      addressesError,
      clientInitializationError,
      cartCreationError,
      updateCartPaymentSessionError,
      loanOptionsError,
    ]

    if (errors.some((error) => error !== null)) {
      console.error("Server errors occurred")
      onServerError()
    }
  }, [
    submitAddressError,
    loanResponseError,
    cartAddressError,
    addressesError,
    clientInitializationError,
    cartCreationError,
    updateCartPaymentSessionError,
    loanOptionsError,
    onServerError,
  ])

  useEffect(() => {
    if (loanResponse && isPaymentSessionUpdated && isShippingMethodSet) {
      setIsReadyToComplete(true)
    } else {
      setIsReadyToComplete(false)
    }
  }, [loanResponse, isPaymentSessionUpdated, isShippingMethodSet])

  const handleOptionSelect = (uuid: string) => {
    setSelectedOptionUuid(uuid)
    sendGoogleAnalyticsEvent("SELECT_LOAN_OPTION", {
      loan_option_id: uuid,
    })
  }

  const isAnyLoading = () =>
    isLoading ||
    isLoanOptionLoading ||
    isuseUpdateCartPaymentSessionLoading ||
    isLoanResponseLoading ||
    isAddressLoading ||
    isSubmitAddressLoading ||
    isCartAddressLoading ||
    isCompleteLoading

  useEffect(() => {
    if (cartDetails) {
      onCartTotalChange(Number((cartDetails.total / 100).toFixed(2)))
    }
  }, [cartDetails, onCartTotalChange])

  const handleClick = async () => {
    setIsCompleteLoading(true)
    await onComplete(cartId!)
    setIsCompleteLoading(false)
  }

  return loanOptions === null ? (
    <LoadingScreen title="Gracias por tu paciencia ..." subtitle="Esto puede tardar unos momentos. Por favor no actualices la página." />
  ) : (
    <LoanOptionsDisplay
      cartId={cartId}
      loanOptions={loanOptions}
      cartDetails={cartDetails}
      onOptionSelect={handleOptionSelect}
      disableButton={!isReadyToComplete}
      isLoading={isAnyLoading()}
      onCompleteLoan={handleClick}
      address={cartAddress}
      loanDetails={loanResponse}
      submitAddress={handleSubmitAddress}
      preSelectedInstallment={preSelectedInstallment}
    />
  )
}

export default LoanOptions
